import { computed } from '@ember/object';
import BaseActualFilterAmplitude from '../../implementation-common/base-actual-filter-amplitude';
import FilterConstants from '../../../../filter-constants';
import * as CommonUtils from '../../../../utils/common';
import * as Complex from '../../../process-data/complex';

export default BaseActualFilterAmplitude.extend({
  c1: null,
  c2: null,
  r1: null,
  r2: null,
  r3: null,
  r4: null,
  whoAmI: 'app/models/implementation/sallen-key/low-pass/actual-filter-amplitude.js',
  complexAlphaPlus: computed('complexDenominator', function () {
    var complexDenominator = this.complexDenominator;
    var complexOne = Complex.createComplex(1, 0);
    var result = complexOne.divide(complexDenominator);
    return result;
  }),
  complexBetaPlus: computed('c1', 'r1', 'complexDenominator', 'sImaginary', function () {
    var complexDenominator = this.complexDenominator;
    var c1 = this.c1;
    var r1 = this.r1;
    var sImaginary = this.sImaginary;
    var result = Complex.createComplex(0, r1 * c1 * sImaginary).divide(complexDenominator);
    return result;
  }),
  complexDenominator: computed('r1', 'r2', 'c1', 'c2', 'sImaginary', 'sSquared', function () {
    var c1 = this.c1;
    var c2 = this.c2;
    var r1 = this.r1;
    var r2 = this.r2;

    var sImaginary = this.sImaginary;
    var sSquared = this.sSquared;
    var denominatorRealTerm = sSquared * r1 * c1 * c2 * r2 + 1;
    var denominatorImaginaryTerm = (r1 * c1 + r1 * c2 + r2 * c2) * sImaginary;
    var result = Complex.createComplex(denominatorRealTerm, denominatorImaginaryTerm);
    return result;
  }),
  complexRParallel: computed(
    'c1',
    'c2',
    'complexDenominator',
    'opAmp.rOut',
    'r1',
    'r2',
    'r3',
    'r4',
    'sImaginary',
    'sSquared',
    function () {
      // compute zlookback this way to avoid so many complex operations
      var complexOne = Complex.createComplex(1, 0);
      var sImaginary = this.sImaginary;
      var sSquared = this.sSquared;
      var complexDenominator = this.complexDenominator;

      var c1 = this.c1;
      var c2 = this.c2;
      var r1 = this.r1;
      var r2 = this.r2;
      var r3 = this.r3;
      var r4 = this.r4;

      var complexZdenom = Complex.createComplex(sSquared * (c1 * c2 * r1 + c1 * c2 * r2), c1 * sImaginary);
      var complexZlookback = complexDenominator.divide(complexZdenom); //complexZdenom variable overwritten

      var rParallelReal = 1 / CommonUtils.parallel(this.opAmp.rOut, r3 + r4);
      var result = complexOne.divide(Complex.createComplex(rParallelReal, 0).plus(complexOne.divide(complexZlookback))); //complexZlookback overwritten
      return result;
    },
  ),
  complexFF: computed('r1', 'complexRParallel', function () {
    var complexRParallel = this.complexRParallel;
    var r1 = this.r1;
    var complexR1 = Complex.createComplex(r1, 0);
    var result = complexRParallel.divide(complexR1);
    return result;
  }),
  complexAlphaMinus: Complex.createComplex(0, 0),
  complexBetaMinus: computed('r3', 'r4', 'sImaginary', function () {
    var r3 = this.r3;
    var r4 = this.r4;

    var sImaginary = this.sImaginary;
    var complexR3 = Complex.createComplex(r3, 0);
    var complexBetaDenom = Complex.createComplex(
      r3 + r4,
      r4 * r3 * FilterConstants.Limits.PARASITIC_INPUT_CAP * sImaginary,
    );
    var result = complexR3.divide(complexBetaDenom);
    return result;
  }),
  inputImpedance: computed(
    'r1',
    'r2',
    'c1',
    'c2',
    'sImaginary',
    'sSquared',
    'complexDenominator',
    'complexAmplitude',
    function () {
      var r1 = this.r1;
      var r2 = this.r2;
      var c1 = this.c1;
      var c2 = this.c2;
      var sImaginary = this.sImaginary;
      var sSquared = this.sSquared;
      var denomTerm1 = sSquared * r1 * c1 * c2 * r2;
      var denomTerm2 = sImaginary * (r1 * c1 + r1 * c2 + r2 * c2);
      var denomTerm3 = 1;
      var denominator = Complex.createComplex(denomTerm1 + denomTerm3, denomTerm2);
      var complexAmplitude = this.complexAmplitude;

      var complexOne = Complex.createComplex(1, 0);
      var complexR1 = Complex.createComplex(r1, 0);

      var term1 = Complex.createComplex(0, r1 * c1 * sImaginary);
      var term2 = Complex.createComplex(1, r2 * c2 * sImaginary);
      term1 = complexOne.plus(complexAmplitude.mult(term1));
      var bottom = denominator.subs(term1.mult(term2));
      var top = complexR1.mult(denominator);
      var result = top.divide(bottom);

      return result;
    },
  ),
});
